import { Injectable } from '@angular/core';
import { NativeDateAdapter } from '@angular/material/core';
import { DateConfigService } from '../services/date-config.service';

/**
 * Custom date adapter for Angular Material that integrates with the application's
 * date formatting configuration system.
 *
 * This adapter extends Angular Material's NativeDateAdapter to provide consistent
 * date formatting and parsing across all Material date components (date pickers,
 * date inputs, etc.) using the centralized DateConfigService.
 *
 * @extends {NativeDateAdapter}
 *
 * @example
 * ```typescript
 * // This adapter is typically configured in providers, not used directly
 * // In your app.config.ts or module:
 * {
 *   provide: DateAdapter,
 *   useClass: AppDateAdapter,
 *   deps: [DateConfigService]
 * }
 *
 * // The adapter automatically handles:
 * // - Date picker display format
 * // - User input parsing
 * // - Form control value formatting
 * ```
 *
 * @remarks
 * This adapter ensures that all Angular Material date components use the same
 * formatting rules as defined in DateConfigService, providing a consistent
 * user experience across the application.
 *
 * Key features:
 * - Automatic MM/dd/yyyy formatting with leading zeros
 * - Robust date parsing for various input formats
 * - Integration with global date configuration
 * - Proper validation of date inputs
 */
@Injectable()
export class AppDateAdapter extends NativeDateAdapter {

  /**
   * Creates an instance of AppDateAdapter.
   *
   * @param {DateConfigService} dateConfigService - Service that provides date formatting configuration
   *
   * @example
   * ```typescript
   * // Typically injected automatically by Angular DI
   * constructor(private dateConfigService: DateConfigService) {
   *   super('en-US'); // Initialize with US locale
   * }
   * ```
   */
  constructor(private dateConfigService: DateConfigService) {
    super('en-US');
  }

  /**
   * Formats a Date object for display in Angular Material components.
   *
   * This method is called automatically by Angular Material components
   * when they need to display a date value.
   *
   * @override
   * @param {Date} date - The Date object to format
   * @param {Object} displayFormat - Display format options (from Material)
   * @returns {string} Formatted date string according to current configuration
   *
   * @example
   * ```typescript
   * // Called automatically by Material components
   * const adapter = new AppDateAdapter(dateConfigService);
   * const date = new Date(2025, 7, 10); // August 10, 2025
   *
   * const formatted = adapter.format(date, {});
   * console.log(formatted); // "08/10/2025" (with default MM/dd/yyyy config)
   *
   * // After changing global config
   * dateConfigService.updateConfig({ displayFormat: 'dd/MM/yyyy' });
   * const formatted2 = adapter.format(date, {});
   * console.log(formatted2); // "10/08/2025"
   * ```
   *
   * @remarks
   * - Returns empty string for null, undefined, or invalid dates
   * - Uses DateConfigService.formatDate() for consistent formatting
   * - Automatically applies leading zeros for months and days
   */
  override format(date: Date, displayFormat: object): string {
    if (!date || isNaN(date.getTime())) return '';
    return this.dateConfigService.formatDate(date);
  }

  /**
   * Parses a string input into a Date object.
   *
   * This method is called when users type dates into Material date inputs
   * or when programmatically setting date values from strings.
   *
   * @override
   * @param {string} value - The string value to parse
   * @returns {Date | null} Parsed Date object or null if parsing fails
   *
   * @example
   * ```typescript
   * const adapter = new AppDateAdapter(dateConfigService);
   *
   * // Valid inputs
   * adapter.parse('08/10/2025'); // Date(2025, 7, 10)
   * adapter.parse('8/10/2025');  // Date(2025, 7, 10) - handles missing leading zero
   * adapter.parse('08-10-2025'); // Date(2025, 7, 10) - handles different separator
   *
   * // Invalid inputs
   * adapter.parse('');           // null
   * adapter.parse('invalid');    // null
   * adapter.parse('13/32/2025'); // null - invalid month/day
   * adapter.parse('02/30/2025'); // null - invalid date (Feb 30)
   * ```
   *
   * @remarks
   * Parsing strategy:
   * 1. First tries to parse using MM/dd/yyyy format with validation
   * 2. Falls back to native JavaScript Date parsing
   * 3. Validates that parsed date components match input
   * 4. Returns null for any invalid or unparseable input
   *
   * Supported input formats:
   * - MM/dd/yyyy (08/10/2025)
   * - M/d/yyyy (8/10/2025)
   * - Separator variations: /, -, .
   */
  override parse(value: string): Date | null {
    if (!value || !value.trim()) return null;

    const config = this.dateConfigService.getCurrentConfig();

    // Handle MM/dd/yyyy format with various separators
    const separators = ['/', '-', '.'];
    let parts: string[] = [];
    let usedSeparator = '';

    // Find which separator is used
    for (const separator of separators) {
      if (value.includes(separator)) {
        parts = value.split(separator);
        usedSeparator = separator;
        break;
      }
    }

    if (parts.length === 3) {
      const month = parseInt(parts[0], 10) - 1; // Month is 0-indexed in Date constructor
      const day = parseInt(parts[1], 10);
      const year = parseInt(parts[2], 10);

      // Validate the parsed values are in reasonable ranges
      if (this.isValidDateComponents(month, day, year)) {
        const date = new Date(year, month, day);

        // Double-check the date is valid (handles cases like Feb 30)
        if (this.isDateComponentsEqual(date, year, month, day)) {
          return date;
        }
      }
    }

    // Fallback to native parsing
    const fallbackDate = new Date(value);
    return isNaN(fallbackDate.getTime()) ? null : fallbackDate;
  }

  /**
   * Validates that date components are within reasonable ranges.
   *
   * @private
   * @param {number} month - Month (0-11, 0-indexed)
   * @param {number} day - Day (1-31)
   * @param {number} year - Full year (e.g., 2025)
   * @returns {boolean} True if components are valid, false otherwise
   *
   * @example
   * ```typescript
   * this.isValidDateComponents(7, 10, 2025);  // true (August 10, 2025)
   * this.isValidDateComponents(12, 10, 2025); // false (month > 11)
   * this.isValidDateComponents(7, 32, 2025);  // false (day > 31)
   * this.isValidDateComponents(7, 10, 1800);  // false (year < 1900)
   * ```
   */
  private isValidDateComponents(month: number, day: number, year: number): boolean {
    return (
      month >= 0 && month <= 11 &&
      day >= 1 && day <= 31 &&
      year >= 1900 && year <= 3000
    );
  }

  /**
   * Checks if a Date object's components match the expected values.
   *
   * This prevents cases where JavaScript Date constructor creates
   * unexpected dates (e.g., Feb 30 becomes Mar 2).
   *
   * @private
   * @param {Date} date - The Date object to check
   * @param {number} expectedYear - Expected full year
   * @param {number} expectedMonth - Expected month (0-indexed)
   * @param {number} expectedDay - Expected day
   * @returns {boolean} True if components match, false otherwise
   *
   * @example
   * ```typescript
   * const date = new Date(2025, 1, 30); // Feb 30, 2025 becomes Mar 2, 2025
   * this.isDateComponentsEqual(date, 2025, 1, 30); // false
   *
   * const validDate = new Date(2025, 1, 28); // Feb 28, 2025
   * this.isDateComponentsEqual(validDate, 2025, 1, 28); // true
   * ```
   */
  private isDateComponentsEqual(date: Date, expectedYear: number, expectedMonth: number, expectedDay: number): boolean {
    return (
      date.getFullYear() === expectedYear &&
      date.getMonth() === expectedMonth &&
      date.getDate() === expectedDay
    );
  }

  /**
   * Gets the first day of the week.
   *
   * @override
   * @returns {number} 0 for Sunday (US standard)
   *
   * @example
   * ```typescript
   * adapter.getFirstDayOfWeek(); // 0 (Sunday)
   * ```
   *
   * @remarks
   * This affects how calendar views display the week.
   * 0 = Sunday, 1 = Monday, etc.
   */
  override getFirstDayOfWeek(): number {
    return 0; // Sunday
  }

  /**
   * Gets an array of date names with leading zeros for calendar display.
   *
   * @override
   * @returns {string[]} Array of date strings from "01" to "31"
   *
   * @example
   * ```typescript
   * adapter.getDateNames();
   * // Returns: ["01", "02", "03", ..., "30", "31"]
   * ```
   *
   * @remarks
   * This ensures consistent display with leading zeros in calendar components.
   */
  override getDateNames(): string[] {
    const dateNames: string[] = [];
    for (let i = 1; i <= 31; i++) {
      dateNames.push(i.toString().padStart(2, '0'));
    }
    return dateNames;
  }
}
